/* ---------------------------------------------------------------------------
   The opt-in, served by us and fed to Klaviyo.

   WHY THIS EXISTS AT ALL

   Klaviyo's form builder cannot bleed an image off a panel, cannot overlap type
   with a photograph, and cannot dock a bar in a product page. Four of the five
   directions we designed are not buildable in it. So the form stops being a
   Klaviyo form and becomes ours, and Klaviyo keeps everything that actually
   matters: the list, the consent record and the flow.

   WHY THAT IS SAFE

   The welcome sequence triggers on ADDED TO LIST, not on Submitted Form. A
   profile added to `polite community` through the public client endpoint
   triggers it exactly as Klaviyo's own popup does. That one fact is what makes
   this whole approach possible, and it is worth re-checking before any deploy:
   if the flow is ever moved onto Submitted Form, every signup here stops
   receiving the welcome and nobody finds out until revenue is down.

   WHAT WE TAKE ON BY LEAVING THE BUILDER

     display rules      here, not in a settings panel
     frequency capping  localStorage, below
     the numbers        custom metrics through /client/events/
     A/B bucketing      not implemented; the test is run in Meta

   ISOLATION

   This file is loaded by a section that only exists on the landing page
   template, so it cannot fire on the control PDP. Klaviyo's own form has to be
   excluded from `?view=wx-lp` at their end, or both appear.

   NO SECRETS HERE. company_id is the public site ID that already ships in the
   storefront script. There is no API key in this file and there must never be.
   --------------------------------------------------------------------------- */
(function () {
  'use strict';

  // Run once, however many times the file is included.
  //
  // Each opt-in section emits its own <script src="wx-optin.js">. That was
  // harmless with one section on the page; the international popup made two,
  // the browser executed the file twice, and every event was sent twice, 2ms
  // apart, by two copies of the same closure.
  //
  // It went unnoticed because the duplication was in the DENOMINATOR: form
  // views read double, so the conversion rate read half. Meanwhile the submit
  // count was reading double for its own unrelated reason, and the two errors
  // roughly cancelled. A right-looking answer from two wrongs is the hardest
  // kind of bug to see.
  //
  // Deduplicating the script tag in Liquid is not enough: sections render
  // independently and neither knows the other exists.
  if (window.__wxOptinLoaded) return;
  window.__wxOptinLoaded = true;

  // A page can carry more than one of these now: the US draw and the
  // international 15% off are two instances of the same section with different
  // country rules. Only one of them can ever apply to a given visitor, so pick
  // that one here rather than wiring the first in the DOM and letting it hide
  // itself while the one that applies stays dead.
  function fits(el) {
    var here = (el.getAttribute('data-country') || '').trim().toUpperCase();
    var no = (el.getAttribute('data-countries-exclude') || '')
      .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
    var yes = (el.getAttribute('data-countries') || '')
      .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
    if (no.length && here && no.indexOf(here) > -1) return false;
    if (!yes.length) return true;
    // No country at all counts as a fit. Shopify normally fills it in, and
    // showing nobody anything because one request came back without a market is
    // the worse of the two failures.
    if (!here) return true;
    return yes.indexOf(here) > -1;
  }

  var root = [].slice.call(document.querySelectorAll('[data-wx-optin]')).filter(fits)[0];
  if (!root) return;

  // The teaser tab is a sibling of the popup, not a child, so it has to be
  // taken from the same section as the root we chose.
  var section = root.closest('.shopify-section') || document;

  var cfg = {
    company: root.dataset.company,
    list: root.dataset.list,
    tz: root.dataset.tz || 'America/Los_Angeles',
    close: root.dataset.close,
    open: root.dataset.open,
    ends: Number(root.dataset.ends || 0),
    delay: Number(root.dataset.delay || 8) * 1000,
    scroll: Number(root.dataset.scroll || 25),
    exit: root.dataset.exit === 'true',
    againAfterClose: Number(root.dataset.againClose || 14),
    againAfterJoin: Number(root.dataset.againJoin || 365),
    lp: root.dataset.lp || '',
    // Named the way the Klaviyo forms are named, because those are ours too:
    // AW was adswise, which is what wiseXPD was called. Calling this one
    // "ours" in a report implies the others are somebody else's.
    form: root.dataset.form || '',
    collect: root.dataset.collect || '',
    capi: root.dataset.capi || '',
    client: root.dataset.client || ''
  };
  if (!cfg.company || !cfg.list) return;      // unconfigured is invisible, not broken

  var K = 'https://a.klaviyo.com/client';
  var REV = '2025-07-15';

  // Where this visitor came from.
  //
  // Neither form was recording it. That matters more than it sounds: the rate we
  // are measured against is built on ALL of Polite's traffic, including people
  // arriving from email and direct, while this page receives nothing but cold
  // paid clicks from one ad set. Cold traffic opts in at a lower rate than warm
  // traffic on any store, so the comparison is tilted against us before the
  // design is considered at all.
  //
  // Klaviyo's own form events carry no utm, so the benchmark cannot be corrected
  // after the fact. Ours can at least be segmented from here on.
  //
  // Only the campaign fields and whether a click id was present. No identifiers,
  // nothing that describes the person.
  var source = (function () {
    try {
      var q = new URLSearchParams(location.search);
      var out = {};
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content'].forEach(function (k) {
        var v = q.get(k);
        if (v) out[k] = String(v).slice(0, 60);
      });
      if (q.get('fbclid')) out.paid_click = 'meta';
      if (q.get('gclid')) out.paid_click = 'google';
      if (!out.utm_source && !out.paid_click) {
        // No campaign tagging at all. Worth recording as its own answer rather
        // than as a gap, so "untagged" and "not looked at" stay distinguishable.
        out.utm_source = document.referrer ? 'referral' : 'direct';
      }
      return out;
    } catch (e) { return {}; }
  })();

  /* ---- memory ------------------------------------------------------------
     Wrapped, because Safari in private mode throws on access rather than
     returning null, and a storage error must never be the reason a page dies. */
  function get(k) { try { return localStorage.getItem(k); } catch (e) { return null; } }
  function set(k, v) { try { localStorage.setItem(k, v); } catch (e) { /* fine */ } }
  function days(k) {
    var t = Number(get(k) || 0);
    return t ? (Date.now() - t) / 86400000 : Infinity;
  }

  function anonId() {
    var a = get('wx_anon');
    if (!a) {
      a = 'wx-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      set('wx_anon', a);
    }
    return a;
  }

  /* ---- Klaviyo -----------------------------------------------------------
     Two public endpoints, both keyless, both the ones Klaviyo's own form uses
     underneath, which is why list membership, consent state and flow triggering
     all behave identically to the form we are replacing. */
  // Page analytics go to our own counter; only a submit goes to Klaviyo.
  //
  // Klaviyo's client endpoint requires a profile on every event, so every view
  // from someone who never submitted was leaving an anonymous profile behind:
  // 118 in the first eight days, unreachable and permanent. A view is not a
  // person. A submit is, and by then we have their email, so it lands on a real
  // profile rather than a husk.
  function collect(event, props) {
    if (!cfg.collect) return;
    var body = Object.assign({
      event: event, form: cfg.form, lp: cfg.lp, visitor: anonId(), country: country,
      // The pathname, so views can be counted per page the way submits already
      // are. Without it, retiring Klaviyo's popup would end per-page rates.
      page: location.pathname,
      // ?wx_test=1 marks a harness run so it is stored but never counted. Fifteen
      // of mine were reported to the client as a 33% conversion rate before this
      // existed.
      // Also a test: headless Chrome (our screenshot crawls, which loaded 632
      // pages on 5-6 Oct and tripled the USA form's views) and any theme that is
      // not the published one, so previewing the sandbox never counts as traffic.
      test: /[?&]wx_test=1/.test(location.search) || /HeadlessChrome/.test(navigator.userAgent) ||
        !!(window.Shopify && Shopify.theme && Shopify.theme.role && Shopify.theme.role !== 'main') || undefined,
    }, source, props || {});
    try {
      // keepalive, so the close event survives the page being dismissed with it.
      fetch(cfg.collect, {
        method: 'POST', keepalive: true, mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).catch(function () { /* analytics must never block a signup */ });
    } catch (e) { /* nor break one */ }
  }

  // Meta only knows what the pixel is told, and nothing was telling it.
  //
  // The pixel is on the page already, Shopify injects it, but this form bypasses
  // everything Shopify and Klaviyo would have fired, so Leads read as a dash in
  // the ads manager and the algorithm had no signal to optimise toward. The
  // event id is shared so a server-side Conversions API call can be deduped
  // against this one later, which is PW-37.
  function metaLead(eventId, props) {
    try {
      if (typeof window.fbq !== 'function') return;
      window.fbq('track', 'Lead', Object.assign({
        content_name: cfg.form, content_category: 'signup',
      }, props || {}), { eventID: eventId });
    } catch (e) { /* a blocked pixel is not our problem to solve */ }
  }

  // The server half of the same Lead, PW-37. The browser event above carries
  // no identifier, and this account matches email on well under one percent
  // of its events. The opt-in is the one moment a first-party email exists
  // before a purchase, so it goes to wiseOS, which hashes it and sends a
  // Conversions API Lead under the same event id. Meta keeps one of the two.
  //
  // Sent from the browser, not relayed later, because Meta scores a website
  // event on the visitor's own IP and user agent, and only this request has
  // them. Fire and forget: a signup never waits on tracking.
  function metaCookie(name) {
    var m = document.cookie.match('(?:^|; )' + name + '=([^;]*)');
    return m ? decodeURIComponent(m[1]) : '';
  }
  function capiLead(eventId, email) {
    if (!cfg.capi || !cfg.client) return;
    try {
      var fbc = metaCookie('_fbc');
      // The pixel writes _fbc from the click id on the URL, but on this store
      // it is present on about half a percent of events. When the click id is
      // on the URL and the cookie is not, build it the way the pixel would
      // have, which is the format Meta documents.
      if (!fbc) {
        var fbclid = (location.search.match(/[?&]fbclid=([^&#]+)/) || [])[1];
        if (fbclid) fbc = 'fb.1.' + Date.now() + '.' + decodeURIComponent(fbclid);
      }
      fetch(cfg.capi, {
        method: 'POST', keepalive: true, mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          client: cfg.client, email: email, event_id: eventId,
          fbp: metaCookie('_fbp') || undefined, fbc: fbc || undefined,
          url: location.href.split('#')[0], form: cfg.form, lp: cfg.lp,
        }),
      }).catch(function () { /* tracking never blocks a signup */ });
    } catch (e) { /* nor breaks one */ }
  }

  function track(metric, props) {
    var body = {
      data: {
        type: 'event',
        attributes: {
          properties: Object.assign({ page: location.pathname, lp: cfg.lp, form: cfg.form }, source, props || {}),
          metric: { data: { type: 'metric', attributes: { name: metric } } },
          profile: { data: { type: 'profile', attributes: { anonymous_id: anonId() } } }
        }
      }
    };
    var email = get('wx_email');
    if (email) body.data.attributes.profile.data.attributes.email = email;
    // keepalive so the close event survives the page being dismissed with it.
    fetch(K + '/events/?company_id=' + encodeURIComponent(cfg.company), {
      method: 'POST', keepalive: true,
      headers: { 'Content-Type': 'application/json', revision: REV },
      body: JSON.stringify(body)
    }).catch(function () { /* analytics must never block a signup */ });
  }

  function subscribe(email, phone, smsConsent) {
    var profile = {
      type: 'profile',
      attributes: {
        email: email,
        anonymous_id: anonId(),
        properties: Object.assign({
          wx_source: 'wx-optin', wx_lp: cfg.lp, wx_form: cfg.form,
        }, source.utm_source ? { wx_utm_source: source.utm_source } : {},
           source.utm_campaign ? { wx_utm_campaign: source.utm_campaign } : {},
           source.paid_click ? { wx_paid_click: source.paid_click } : {}),
        subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
      }
    };
    if (phone) {
      profile.attributes.phone_number = phone;
      if (smsConsent) profile.attributes.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
    }
    return fetch(K + '/subscriptions/?company_id=' + encodeURIComponent(cfg.company), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: REV },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: { profile: { data: profile } },
          relationships: { list: { data: { type: 'list', id: cfg.list } } }
        }
      })
    }).then(function (r) {
      // 202 is the success. Anything else carries a body worth surfacing.
      if (r.status === 202) return { ok: true };
      return r.json().catch(function () { return {}; }).then(function (d) {
        var detail = d && d.errors && d.errors[0] && (d.errors[0].detail || d.errors[0].title);
        return { ok: false, message: detail || ('Klaviyo returned ' + r.status) };
      });
    });
  }

  /* ---- the clock --------------------------------------------------------- */
  var teaser = section.querySelector('[data-wx-teaser]');
  var teaserText = teaser && teaser.querySelector('[data-wx-teaser-text]');

  var clockState = { live: true, over: false };
  function tickClock() {
    if (!window.wxSchedule) return;
    clockState = window.wxSchedule.paint(root, cfg);
    if (clockState.over || !clockState.live) { hide(true); hideTeaser(); return; }
    if (teaserText && clockState.parts) {
      var tpl = teaser.getAttribute('data-template') || '';
      var d = clockState.parts[0];
      teaserText.textContent = tpl
        .replace('[days]', d)
        // "1 days left" is the kind of thing nobody notices until a customer does.
        .replace(/\b1 days\b/, '1 day');
    }
  }

  /* ---- the teaser --------------------------------------------------------
     Small, on the side edge, and only for somebody who has not joined. It is
     the way back in for a visitor who dismissed the popup or is inside the
     quiet period, which is otherwise a dead end for the rest of their visit.
     It never appears at the bottom: the buy bar lives there and a teaser over
     the add-to-bag button costs more than it earns. */
  function showTeaser() {
    if (!teaser) return;
    if (clockState.over || !clockState.live) return;
    if (days('wx_optin_joined') < cfg.againAfterJoin) return;   // they are already in
    if (open) return;
    teaser.hidden = false;
    requestAnimationFrame(function () { teaser.classList.add('is-in'); });
  }
  function hideTeaser() {
    if (!teaser) return;
    teaser.classList.remove('is-in');
    setTimeout(function () { teaser.hidden = true; }, 320);
  }
  if (teaser) {
    teaser.addEventListener('click', function () {
      collect('teaser');
      hideTeaser();
      reveal();
    });
  }

  /* ---- steps ------------------------------------------------------------- */
  var steps = [].slice.call(root.querySelectorAll('[data-wx-step]'));
  function show(n) {
    steps.forEach(function (s) { s.hidden = Number(s.dataset.wxStep) !== n; });
    collect('step', { step: n });
  }

  var open = false;
  function reveal() {
    if (open) return;
    if (clockState.over || !clockState.live) return;
    open = true;
    root.hidden = false;
    // Two frames, so the transition has something to move from.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { root.classList.add('is-open'); });
    });
    hideTeaser();
    set('wx_optin_seen', String(Date.now()));
    collect('viewed');
    var first = root.querySelector('input:not([type=checkbox])');
    if (first) setTimeout(function () { first.focus(); }, 380);
  }

  function hide(silent) {
    if (!open && silent) { root.hidden = true; return; }
    open = false;
    root.classList.remove('is-open');
    setTimeout(function () { root.hidden = true; }, 320);
    if (!silent) {
      set('wx_optin_closed', String(Date.now()));
      collect('closed');
    }
    // Dismissing the popup is not "no". It is "not now", and the teaser is what
    // makes that recoverable. Once they have joined, showTeaser declines.
    setTimeout(showTeaser, 500);
  }

  /* ---- when to show it ---------------------------------------------------
     Klaviyo's builder owned this. Everything below is what that panel was
     doing, written out. */
  // Frequency capping lives in this browser's localStorage, which is exactly
  // what you want in production and exactly what makes the thing untestable:
  // join once and it is gone for a year, on that machine, with no way back.
  //
  //   ?wx_optin=reset   forget this browser ever saw it, then behave normally
  //   ?wx_optin=force   show it regardless, without clearing anything
  //   ?wx_optin=off     never show it
  //
  // Safe to leave in. None of them affect a visitor who does not type one, and
  // the alternative is telling someone to open devtools and delete four keys.
  var urlMode = (location.search.match(/[?&]wx_optin=(off|reset|force)/) || [])[1];
  if (urlMode === 'reset') {
    ['wx_optin_joined', 'wx_optin_closed', 'wx_optin_seen', 'wx_email']
      .forEach(function (k) { try { localStorage.removeItem(k); } catch (e) { /* fine */ } });
  }

  // Countries the offer is actually open to. Blank means everywhere, so a page
  // that does not set it behaves exactly as before.
  //
  // This is checked before the frequency capping and before anything is
  // tracked, so a visitor outside the offer produces no wx_form_viewed either.
  // That is the point: if they counted as a view, our conversion rate would be
  // measured against an audience the form we are compared to never sees.
  var allowed = (root.dataset.countries || '')
    .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);
  var country = (root.dataset.country || '').trim().toUpperCase();

  var blocked = (root.dataset.countriesExclude || '')
    .split(',').map(function (c) { return c.trim().toUpperCase(); }).filter(Boolean);

  function outsideOffer() {
    // A page with its own sign-up opts out entirely, no popup and no tab: the
    // BFCM page (Lau, 24 Oct) must not offer a 15% welcome code over its own list.
    if (document.querySelector('[data-wx-no-optin]')) return true;
    // An allowlist cannot say "everywhere except the US", which is what the
    // international offer is, so there is a blocklist too. Blocklist first: if
    // a country is named in both, the exclusion is the more specific statement.
    if (blocked.length && country && blocked.indexOf(country) > -1) return true;
    if (!allowed.length) return false;
    // No country at all is treated as inside. Shopify normally fills this in,
    // and silently hiding the only opt-in on the page because one request came
    // back without a market is the worse failure of the two.
    if (!country) return false;
    return allowed.indexOf(country) === -1;
  }

  // Does Klaviyo already know this browser?
  //
  // klaviyo.isIdentified() returns a PROMISE, not a boolean. The first version
  // of this compared it to true, which a Promise never equals, so the rule was
  // inert from the day it was written and we showed the popup to every existing
  // subscriber. The Klaviyo form we are measured against excludes them, so our
  // denominator was full of people who had already signed up and would never
  // sign up again, and our conversion rate was biased down against a benchmark
  // that had no such passengers.
  //
  // Resolved once, up front, and cached. The storefront script loads async so it
  // may not be there yet; polled for a few seconds rather than assumed.
  // Unknown counts as not identified: showing a popup to someone who has already
  // subscribed is a smaller cost than hiding it from someone who has not.
  var identified = null;
  (function watchIdentity(tries) {
    try {
      if (window.klaviyo && typeof klaviyo.isIdentified === 'function') {
        var v = klaviyo.isIdentified();
        if (v && typeof v.then === 'function') {
          v.then(function (r) { identified = (r === true); },
                 function () { identified = false; });
        } else {
          identified = (v === true);
        }
        return;
      }
    } catch (e) { identified = false; return; }
    if ((tries || 0) < 40) setTimeout(function () { watchIdentity((tries || 0) + 1); }, 300);
    else identified = false;
  })();

  function suppressed() {
    if (urlMode === 'off') return 'switched off by url';
    if (outsideOffer()) return 'outside the offer, ' + country;
    if (urlMode === 'force') return null;
    if (days('wx_optin_joined') < cfg.againAfterJoin) return 'joined';
    if (days('wx_optin_closed') < cfg.againAfterClose) return 'closed recently';
    if (identified === true) return 'already identified';
    return null;
  }

  function arm() {
    // Outside the offer means nothing at all, not even the teaser tab. Someone
    // who is dismissed or already joined still gets the tab, because the offer
    // is open to them and they may come back to it.
    if (outsideOffer()) return;
    var why = suppressed();
    if (why) { showTeaser(); return; }
    var fired = false;
    function go() { if (!fired) { fired = true; reveal(); } }

    setTimeout(showTeaser, 1200);
    setTimeout(go, cfg.delay);

    if (cfg.scroll > 0) {
      var onScroll = function () {
        var h = document.documentElement.scrollHeight - window.innerHeight;
        if (h > 0 && (window.scrollY / h) * 100 >= cfg.scroll) {
          window.removeEventListener('scroll', onScroll);
          go();
        }
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }

    // Exit intent is a desktop idea. A phone has no cursor to leave with, and
    // the pointer check keeps it off touch devices that happen to be wide.
    if (cfg.exit && matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('mouseout', function (e) {
        if (!e.relatedTarget && e.clientY <= 0) go();
      });
    }
  }

  /* ---- wiring ------------------------------------------------------------ */
  var msg = root.querySelector('[data-wx-msg]');
  function say(text, bad) {
    if (!msg) return;
    msg.textContent = text || '';
    msg.hidden = !text;
    msg.classList.toggle('is-bad', !!bad);
  }

  root.addEventListener('click', function (e) {
    if (e.target.closest('[data-wx-close]')) { e.preventDefault(); hide(); }
    if (e.target === root) hide();                        // the scrim
    if (e.target.closest('[data-wx-skip]')) { e.preventDefault(); show(3); }
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && open) hide();
  });

  var emailForm = root.querySelector('[data-wx-email-form]');
  if (emailForm) emailForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var input = emailForm.querySelector('input[type=email]');
    var email = (input.value || '').trim();
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { say('That email does not look right.', true); return; }
    var btn = emailForm.querySelector('button');
    btn.disabled = true; say('');
    subscribe(email, null, false).then(function (res) {
      btn.disabled = false;
      if (!res.ok) { say(res.message, true); return; }
      set('wx_email', email);
      set('wx_optin_joined', String(Date.now()));
      var eid = 'wx-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
      track('wx_form_submitted', { step: 1, event_id: eid });
      collect('submitted', { step: 1 });
      metaLead(eid);
      capiLead(eid, email);
      show(2);
    }).catch(function () {
      btn.disabled = false;
      say('That did not go through. Try again in a moment.', true);
    });
  });

  var phoneForm = root.querySelector('[data-wx-phone-form]');
  if (phoneForm) phoneForm.addEventListener('submit', function (e) {
    e.preventDefault();
    var input = phoneForm.querySelector('input[type=tel]');
    var consent = phoneForm.querySelector('input[type=checkbox]');
    var phone = (input.value || '').trim();
    if (!phone) { show(3); return; }
    // Klaviyo wants E.164. A bare US number is the common case on this form.
    if (!/^\+/.test(phone)) {
      var digits = phone.replace(/\D/g, '');
      if (digits.length === 10) phone = '+1' + digits;
      else if (digits.length === 11 && digits[0] === '1') phone = '+' + digits;
      else { say('Add the country code, for example +1 for the USA.', true); return; }
    }
    var btn = phoneForm.querySelector('button');
    btn.disabled = true; say('');
    subscribe(get('wx_email'), phone, consent && consent.checked).then(function (res) {
      btn.disabled = false;
      if (!res.ok) { say(res.message, true); return; }
      // No second Lead. The person was already counted at step one; firing
      // again here would report two leads for one human and teach Meta to
      // optimise toward a phone number we are not selling.
      track('wx_form_submitted', { step: 2, sms: !!(consent && consent.checked) });
      collect('submitted', { step: 2 });
      show(3);
    }).catch(function () {
      btn.disabled = false;
      say('That did not go through, but your entry is already in.', true);
    });
  });

  /* ---- the inline bar on the product page --------------------------------
     Same submitter, so the two surfaces cannot drift apart in what they write
     to Klaviyo. The bar is a single field, so it is step one and nothing more. */
  document.querySelectorAll('[data-wx-form="draw"]').forEach(function (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var input = form.querySelector('input[type=email]');
      var email = (input.value || '').trim();
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { input.focus(); return; }
      var btn = form.querySelector('button');
      var was = btn.textContent;
      btn.disabled = true; btn.textContent = 'One moment';
      subscribe(email, null, false).then(function (res) {
        if (!res.ok) { btn.disabled = false; btn.textContent = was; return; }
        set('wx_email', email);
        set('wx_optin_joined', String(Date.now()));
        var beid = 'wx-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
        track('wx_form_submitted', { step: 1, surface: 'pdp-bar', event_id: beid });
        collect('submitted', { step: 1 });
        metaLead(beid);
        capiLead(beid, email);
        form.innerHTML = '<p class="wx-draw__done">' +
          (form.dataset.wxDone || 'You are in. Check your inbox.') + '</p>';
      }).catch(function () { btn.disabled = false; btn.textContent = was; });
    });
  });

  /* ---- go ---------------------------------------------------------------- */
  // An offer that is not the weekly draw has no clock to run. The international
  // popup is a standing 15% off, so there is no countdown in its markup and
  // nothing for wxSchedule to paint. Left running it would read this popup's
  // missing schedule attributes, decide the offer was not live, and hide it.
  if (root.dataset.schedule !== 'off') {
    tickClock();
    setInterval(tickClock, 1000);
  }
  show(1);
  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
